// The webtop's background: its colour or image (on its own layer, so only it blurs), the browser chrome colour
// that follows it, and the Background submenu (Color, Image, Blur, Remove Image, Reset). An image is one of the
// webtop's assets (applets/storage/src/assets.js: uploaded, or fetched by the server from an address) or an address, written into the
// record with a PATCH like every other field.
import { assetSrc, putAsset } from './applets.js';
import { colorMenu } from './color.js';
import { submenu } from './menu.js';
import { overlayColor } from './overlay.js';
import { render } from './render.js';
import { API, $webtop, webtop, setWebtop } from './state.js';
import { api, modal, toast } from './ui.js';

const $bgFile = document.getElementById('bg-file');

// The colour goes on the webtop itself; an image goes on its own layer underneath the tiles, so it can be
// blurred without blurring anything else.
// The browser and phone chrome (theme-color: Android's bars, Safari's toolbar tint) and the page behind the
// webtop (what shows in iOS overscroll and around the safe areas) take the webtop's colour, or the client's own;
// an open overlay that paints the whole screen (the launcher on a phone) lends them its colour meanwhile, and while a
// modal dialog is open the webtop's colour gives way to the client's own (style.css hides the webtop's background).
const $themeColor = document.querySelector('meta[name="theme-color"]');
let chromeColor = null;
export function applyChromeColor(c) {
  chromeColor = c || null;
  paintChrome();
}
export function paintChrome() {
  const c = overlayColor() || (document.querySelector('dialog:modal') ? null : chromeColor) || themeBg();
  $themeColor.content = c;
  document.documentElement.style.backgroundColor = c;
}
export function applyBackground() {
  const bg = webtop.background || {};
  const hasImage = bg.type === 'image' && !!bg.value;
  $webtop.style.background = (hasImage ? bg.color : bg.value) || '';
  applyChromeColor(hasImage ? bg.color : bg.value);
  let $bg = $webtop.querySelector('.bg');
  if (!hasImage) { $bg?.remove(); return; }
  if (!$bg) { $bg = document.createElement('div'); $bg.className = 'bg'; $bg.setAttribute('aria-hidden', 'true'); }
  $bg.classList.toggle('blur', !!bg.blur);
  $webtop.prepend($bg);
  // The image is set once its address is resolved (an asset kept in this browser is read from its storage), and only if
  // the webtop still shows that image by then.
  const layer = $bg;
  assetSrc(bg.value).then((src) => { if (webtop?.background?.value === bg.value && layer.isConnected) layer.style.backgroundImage = `url("${encodeURI(src)}")`; });
}

// The theme's webtop background (--bg in style.css), read live so a theme switch is picked up.
const themeBg = () => getComputedStyle(document.documentElement).getPropertyValue('--bg').trim().toLowerCase();
export function backgroundMenu() {
  submenu(() => {
    const bg = webtop.background || {}; // null background = nothing custom, the client's own colour shows
    const hasImage = bg.type === 'image';
    return [
      { label: 'Background', cls: 'head' },
      { label: 'Color', sub: true, action: () => colorMenu(backgroundColor) },
      { label: 'Image', action: imageDialog },
      ...(hasImage ? [{ label: 'Blur', checked: !!bg.blur, stay: true, action: toggleBlur }] : []),
      'sep',
      ...(hasImage ? [{ label: 'Remove Image', action: removeBackgroundImage }] : []),
      { label: 'Reset', cls: 'danger', action: resetBackground }, // always offered: back to the client's own colour
    ];
  });
}
// Blur applies to the image layer only; the setting rides along on the background object.
async function toggleBlur() {
  const bg = webtop.background;
  if (bg?.type !== 'image') return;
  try { setWebtop(await api('PATCH', API, { background: { ...bg, blur: !bg.blur } })); render(); } catch (e) { toast(e); }
}

export function currentColor() {
  const bg = webtop.background || {};
  return bg.type === 'color' ? bg.value : (bg.color || themeBg());
}
// The custom colour set (on its own, or under an image), or null when none is.
const customColor = () => { const bg = webtop.background || {}; return (bg.type === 'color' ? bg.value : bg.color) || null; };

// The background's colour (on its own, or under an image) as a colour target.
const backgroundColor = {
  title: 'Background Color',
  get() { const bg = webtop.background || {}; return (bg.type === 'color' ? bg.value : bg.color) || null; },
  fallback: themeBg,
  preview(c) {
    const bg = webtop.background || {};
    if (bg.type === 'image') bg.color = c; else webtop.background = c ? { type: 'color', value: c } : null;
    applyBackground();
  },
  async save(c) {
    // Clear, and the client's own colour, store nothing, so the webtop keeps following the client if that colour changes.
    if (c === themeBg()) c = null;
    const bg = webtop.background || {};
    const next = bg.type === 'image' ? { ...bg, color: c } : c ? { type: 'color', value: c } : null;
    setWebtop(await api('PATCH', API, { background: next }));
    render();
  },
};

// Image: a dialog that takes an image URL (the server fetches it and keeps a copy, like an upload; a webtop stored in
// this browser keeps the address) or uploads a file from the computer. Either becomes an asset of the webtop
// (putAsset), then its address is written as the background over the colour in effect, keeping the blur.
async function setImage(source) {
  const { url } = await putAsset(source);
  setWebtop(await api('PATCH', API, { background: { type: 'image', value: url, color: customColor(), blur: !!webtop.background?.blur } }));
  render();
}
const $imageDlg = modal(document.getElementById('image-dlg'));
const $imageForm = document.getElementById('image-form');
const $imageErr = document.getElementById('image-error');
function imageDialog() {
  $imageErr.hidden = true;
  $imageForm.reset();
  $imageDlg.showModal();
  $imageForm.elements.url.focus();
}
const imageError = (msg) => { $imageErr.textContent = msg; $imageErr.hidden = false; };

// Drop the image, keeping the custom colour (the store deletes the asset nothing refers to any more).
async function removeBackgroundImage() {
  const color = customColor();
  try { setWebtop(await api('PATCH', API, { background: color ? { type: 'color', value: color } : null })); render(); } catch (e) { toast(e); }
}
// Drop the image and the custom colour: back to the plain default background.
async function resetBackground() {
  try { setWebtop(await api('PATCH', API, { background: null })); render(); toast('Background reset'); } catch (e) { toast(e); }
}

// Event wiring and first drawing, run once by client.js in the order the parts of the page depend on.
export function init() {
  // Whoever opens a dialog (the page, a widget's Edit, an applet), showModal() sets this attribute and close() takes it off.
  new MutationObserver(paintChrome).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
  document.getElementById('image-upload').addEventListener('click', () => $bgFile.click());
  $bgFile.addEventListener('change', async () => {
    const f = $bgFile.files[0];
    $bgFile.value = '';
    if (!f) return;
    $imageDlg.close();
    if (!f.type.startsWith('image/')) return toast('Pick an image file', { kind: 'error' });
    toast('Uploading background…');
    try {
      await setImage(f);
      toast('Background updated');
    } catch (e) { toast(e); }
  });
  $imageForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    let url = $imageForm.elements.url.value.trim();
    if (!url) { imageError('Enter an image URL.'); return $imageForm.elements.url.focus(); }
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    try { new URL(url); } catch { imageError('That is not a valid URL.'); return $imageForm.elements.url.select(); }
    const $fetch = $imageForm.querySelector('button[type=submit]');
    $fetch.disabled = true;
    $imageErr.hidden = true;
    try {
      await setImage(url);
      $imageDlg.close();
      toast('Background updated');
    } catch (err) { imageError(err.message); } finally { $fetch.disabled = false; }
  });
}
